import { Injectable } from "@angular/core";
import { Zone, ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { lastValueFrom } from "rxjs";
import { CommonTeamsService } from "./common-teams.service";

export enum TeamHierarchyNodeType {
    Zone,
    Team,
}

export interface IBaseTeamHierarchyNode {
    type: TeamHierarchyNodeType;
    name: string;
    entity?: Team;
}

export interface ITeamHierarchyNode extends IBaseTeamHierarchyNode {
    id: string | number;
    // needed for dx treeview as the IDs must be unique,
    // but the same team can appear multiple times in the hierarchy
    hierarchyId: string;
    expanded: boolean;
    selected: boolean;
    isParentOfTeam: boolean;
    icon?: string;
    colour?: string;
    children: ITeamHierarchyNode[];
}

@Injectable({
    providedIn: "root",
})
export class TeamHierarchyService {
    constructor(private commonTeamsService: CommonTeamsService) {}

    /** Collect every parent path, including inactive intermediate groups and multiple locations. */
    public static getAncestorTeamIds(teamIds: Iterable<number>, locations: readonly TeamLocation[]): Set<number> {
        const parents = new Map<number, number[]>();
        for (const location of locations) {
            if (location.parentTeamId != null) {
                const ids = parents.get(location.teamId) ?? [];
                ids.push(location.parentTeamId);
                parents.set(location.teamId, ids);
            }
        }

        const pending = [...teamIds];
        const visited = new Set<number>();
        const ancestors = new Set<number>();
        while (pending.length > 0) {
            const teamId = pending.pop()!;
            if (visited.has(teamId)) {
                continue;
            }
            visited.add(teamId);
            for (const parentId of parents.get(teamId) ?? []) {
                ancestors.add(parentId);
                pending.push(parentId);
            }
        }
        return ancestors;
    }

    private async loadHierarchyData() {
        const [rawLocations, allTeams] = await Promise.all([
            lastValueFrom(this.commonTeamsService.getAllTeamLocations()),
            this.commonTeamsService.promiseToGetAllTeams(),
        ]);
        const allLocations = rawLocations.filter((l) => l.parentTeamId != null || l.zone != null);
        const ancestors = TeamHierarchyService.getAncestorTeamIds(allTeams.filter((t) => t.isActive()).map((t) => t.teamId), allLocations);
        const teamMap = new Map<number, Team>(allTeams
            .filter((t) => t.isActive() || ancestors.has(t.teamId))
            .map((t) => [t.teamId, t]));
        return { allLocations, teamMap };
    }

    public async promiseToBuildHierarchyForTeam(team: Team): Promise<ITeamHierarchyNode[]> {
        const { allLocations, teamMap } = await this.loadHierarchyData();
        const ancestorTeamIds = this.findAncestorTeamIds(team.teamId, allLocations);
        const rootNodesMap = new Map<Zone | number, ITeamHierarchyNode>();

        const addZoneRoot = (zone: Zone) => {
            if (!rootNodesMap.has(zone)) {
                rootNodesMap.set(zone, this.buildZoneNode(zone, allLocations, teamMap, team.teamId, ancestorTeamIds));
            }
        };

        const addTeamRoot = (rootTeamId: number) => {
            if (!rootNodesMap.has(rootTeamId)) {
                const rootTeam = teamMap.get(rootTeamId);
                if (rootTeam) {
                    rootNodesMap.set(rootTeamId, this.buildTeamNode(rootTeam, allLocations, teamMap, new Set(), team.teamId, ancestorTeamIds));
                }
            }
        };

        // Find all locations for this team to determine its root parents
        const teamLocations = allLocations.filter((l) => l.teamId === team.teamId);
        for (const location of teamLocations) {
            if (location.zone != null) {
                // Root is a zone
                addZoneRoot(location.zone);
            } else if (location.parentTeamId != null) {
                // Root is a parent team, walk up to find the true root
                const roots = this.findRootsForParentTeam(location.parentTeamId, allLocations, new Set([team.teamId]));
                for (const root of roots) {
                    if ("zone" in root) {
                        addZoneRoot(root.zone);
                    } else {
                        addTeamRoot(root.teamId);
                    }
                }
            }
        }

        // If team has no locations at all, show it as a global (parentless) team
        if (teamLocations.length === 0) {
            addTeamRoot(team.teamId);
        }

        // sort the root nodes
        const nodes = Array.from(rootNodesMap.values()).sort((a, b) => {
            if (a.type === TeamHierarchyNodeType.Zone && b.type === TeamHierarchyNodeType.Zone) {
                return ZoneMetadata.Ordinal[a.id as Zone] - ZoneMetadata.Ordinal[b.id as Zone];
            } else if (a.type === TeamHierarchyNodeType.Zone) {
                return -1;
            } else if (b.type === TeamHierarchyNodeType.Zone) {
                return 1;
            } else {
                return a.name.localeCompare(b.name);
            }
        });

        return nodes;
    }

    public async promiseToGetAncestorTeamIds(team: Team): Promise<Set<number>> {
        const { ancestorTeamIds } = await this.promiseToGetAncestorAndDescendantTeamIds(team);
        return ancestorTeamIds;
    }

    public async promiseToGetDescendantTeamIds(team: Team): Promise<Set<number>> {
        const { descendantTeamIds } = await this.promiseToGetAncestorAndDescendantTeamIds(team);
        return descendantTeamIds;
    }

    public async promiseToGetAncestorAndDescendantTeamIds(team: Team) {
        const { allLocations } = await this.loadHierarchyData();
        return {
            ancestorTeamIds: this.findAncestorTeamIds(team.teamId, allLocations),
            descendantTeamIds: this.findDescendantTeamIds(team.teamId, allLocations),
        };
    }

    public async promiseToGetImmediateParents(team: Team): Promise<IBaseTeamHierarchyNode[]> {
        // if this is a dummy team (for previewing team appearance), then skip getting parents
        if (team.teamId === undefined) {
            return [];
        }

        const locations = await lastValueFrom(this.commonTeamsService.getTeamLocations(team));
        const parents: IBaseTeamHierarchyNode[] = [];

        for (const location of locations) {
            if (location.zone != null) {
                parents.push({
                    type: TeamHierarchyNodeType.Zone,
                    name: ZoneMetadata.Name[location.zone],
                });
            }
            if (location.parentTeam) {
                parents.push({
                    type: TeamHierarchyNodeType.Team,
                    name: location.parentTeam.name,
                    entity: location.parentTeam,
                });
            }
        }

        return parents;
    }

    /** Follow every parent location, retaining all zone and global roots. */
    private findRootsForParentTeam(teamId: number, allLocations: TeamLocation[], visited: Set<number>): ({ zone: Zone } | { teamId: number })[] {
        if (visited.has(teamId)) {
            return [];
        }
        const nextVisited = new Set([...visited, teamId]);
        const locations = allLocations.filter((location) => location.teamId === teamId);
        if (locations.length === 0) {
            return [{ teamId }];
        }

        return locations.flatMap((location): ({ zone: Zone } | { teamId: number })[] => {
            if (location.zone != null) {
                return [{ zone: location.zone }];
            }
            return location.parentTeamId != null
                ? this.findRootsForParentTeam(location.parentTeamId, allLocations, nextVisited)
                : [];
        });
    }

    /**
     * Collects all ancestor team IDs for the given team (walking up through parentTeamId chains).
     */
    private findAncestorTeamIds(teamId: number, allLocations: TeamLocation[]): Set<number> {
        return TeamHierarchyService.getAncestorTeamIds([teamId], allLocations);
    }

    /**
     * Collects all descendant team IDs for the given team (walking down through parentTeamId chains).
     */
    private findDescendantTeamIds(teamId: number, allLocations: TeamLocation[], visited = new Set<number>()): Set<number> {
        const descendants = new Set<number>();
        for (const location of allLocations.filter((l) => l.parentTeamId === teamId)) {
            if (!visited.has(location.teamId)) {
                descendants.add(location.teamId);
                visited.add(location.teamId);
                for (const descendant of this.findDescendantTeamIds(location.teamId, allLocations, visited)) {
                    descendants.add(descendant);
                }
            }
        }
        return descendants;
    }

    private buildZoneNode(zone: Zone, allLocations: TeamLocation[], teamMap: Map<number, Team>, currentTeamId: number, ancestorTeamIds: Set<number>, includeChildren = true): ITeamHierarchyNode {
        // A zone is a parent of the team if the team itself or any of its ancestors are located directly under this zone
        const isParentOfTeam = allLocations.some((l) => l.zone === zone && l.parentTeamId == null && (l.teamId === currentTeamId || ancestorTeamIds.has(l.teamId)));

        return {
            type: TeamHierarchyNodeType.Zone,
            id: zone,
            hierarchyId: zone,
            name: ZoneMetadata.Name[zone],
            icon: ZoneMetadata.Icon[zone],
            colour: ZoneMetadata.getZoneColour(zone),
            expanded: false,
            selected: false,
            isParentOfTeam,
            children: includeChildren
                ? this.getChildTeamNodes(null, zone, allLocations, teamMap, new Set(), currentTeamId, ancestorTeamIds, zone)
                : [],
        };
    }

    private buildTeamNode(team: Team, allLocations: TeamLocation[], teamMap: Map<number, Team>, visited: Set<number>, currentTeamId: number, ancestorTeamIds: Set<number>, includeChildren = true, hierarchyPrefix?: string): ITeamHierarchyNode {
        const newVisited = new Set([...visited, team.teamId]);
        const expanded = team.teamId === currentTeamId || ancestorTeamIds.has(team.teamId);
        const suffix = [...visited, team.teamId].join("_");
        return {
            type: TeamHierarchyNodeType.Team,
            id: team.teamId,
            hierarchyId: hierarchyPrefix ? `${hierarchyPrefix}_${suffix}` : suffix,
            name: team.name,
            icon: team.iconClass,
            entity: team,
            expanded,
            selected: team.teamId === currentTeamId,
            isParentOfTeam: ancestorTeamIds.has(team.teamId),
            children: includeChildren
                ? this.getChildTeamNodes(team.teamId, null, allLocations, teamMap, newVisited, currentTeamId, ancestorTeamIds, hierarchyPrefix)
                : [],
        };
    }

    private getChildTeamNodes(
        parentTeamId: number | null,
        parentZone: Zone | null,
        allLocations: TeamLocation[],
        teamMap: Map<number, Team>,
        visited: Set<number>,
        currentTeamId: number,
        ancestorTeamIds: Set<number>,
        hierarchyPrefix?: string,
    ): ITeamHierarchyNode[] {
        const childLocations = allLocations.filter((teamLocation) => {
            return parentZone != null
                ? teamLocation.zone === parentZone && teamLocation.parentTeamId == null
                : teamLocation.parentTeamId === parentTeamId;
        });

        return childLocations
            .filter((l) => teamMap.has(l.teamId) && !visited.has(l.teamId))
            .sort(SortUtilities.getSortByFieldFunction<TeamLocation>("ordinal"))
            .map((l) => this.buildTeamNode(teamMap.get(l.teamId)!, allLocations, teamMap, visited, currentTeamId, ancestorTeamIds, true, hierarchyPrefix));
    }
}
